import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DOMPurify from 'dompurify'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'

import type { ComponentPropsWithoutRef, ReactNode } from 'react'

/**
 * The building blocks of an agenda item's detail, shared by the appointment and the admission
 * ("opname") detail pages. The root stacks the sections; the parts are atomic and take children,
 * so the page decides which ones to compose, in which order, and lays rows out with the kit's Flex
 * primitives. Nothing here knows an appointment from an admission, which is why it lives in shared.
 *
 * Colour flows down: the root sets the default text colour, a Row sets a tone for the icon and the
 * fact inside it, and every part still takes a colour prop for the odd override.
 */

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail
 * ------------------------------------------------------------------------------------------------- */

type AgendaDetailRootProps = ComponentPropsWithoutRef<'div'>

const AgendaDetailRoot = ({ children, ...props }: AgendaDetailRootProps) => <StRoot {...props}>{children}</StRoot>

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Title
 * ------------------------------------------------------------------------------------------------- */

// The eyebrow is the kind of item above the title ("Opname"), with its icon, in the smaller heading
// size and the secondary colour. The heading stacks it above the name while keeping it part of the
// heading: the outline then reads "Opname Stomatologie", not "Stomatologie".
type AgendaDetailTitleProps = ComponentPropsWithoutRef<typeof H2> & {
  eyebrow?: ReactNode
  eyebrowIcon?: ComponentPropsWithoutRef<typeof FontAwesomeIcon>['icon']
}

const AgendaDetailTitle = ({ eyebrow, eyebrowIcon, children, ...props }: AgendaDetailTitleProps) => (
  <StTitle noMargin {...props}>
    {eyebrow ? (
      <StEyebrow>
        {eyebrowIcon ? <StIcon icon={eyebrowIcon} /> : null}
        {eyebrow}
      </StEyebrow>
    ) : null}
    {children}
  </StTitle>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Icon
 * ------------------------------------------------------------------------------------------------- */

// The small icon in front of a fact row. The icon comes from the call site; the colour from the row.
// It sits in a slot as tall as one line of the fact, so it lines up with the first line and a fact
// that wraps keeps its icon at the top.
type AgendaDetailIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const AgendaDetailIcon = (props: AgendaDetailIconProps) => (
  <StIconSlot>
    <StIcon {...props} />
  </StIconSlot>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Row
 * ------------------------------------------------------------------------------------------------- */

// An icon and a fact side by side. The tone colours both: `highlight` is the tinted block for a
// fact that is not known yet, such as an hour still to be set, `warning` a time the patient must act on.
// The highlight's padding bleeds into the margin so the icons of plain and highlighted rows still
// line up with the title.
type AgendaDetailTone = 'default' | 'highlight' | 'warning'

type AgendaDetailRowProps = ComponentPropsWithoutRef<'div'> & {
  tone?: AgendaDetailTone
}

const AgendaDetailRow = ({ tone = 'default', children, ...props }: AgendaDetailRowProps) => (
  <StRow $tone={tone} {...props}>
    {children}
  </StRow>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Fact
 * ------------------------------------------------------------------------------------------------- */

// One fact about the item next to its icon: the date, the time, the room.
type AgendaDetailFactProps = ComponentPropsWithoutRef<typeof Span>

const AgendaDetailFact = ({ children, ...props }: AgendaDetailFactProps) => (
  <Span type='BodyLargeRegular' {...props}>
    {children}
  </Span>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.WaitTime
 * ------------------------------------------------------------------------------------------------- */

type AgendaDetailWaitTimeProps = ComponentPropsWithoutRef<typeof Span>

const AgendaDetailWaitTime = ({ children, ...props }: AgendaDetailWaitTimeProps) => (
  <StWaitTime type='BodyLargeRegular' {...props}>
    {children}
  </StWaitTime>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.SectionTitle
 * ------------------------------------------------------------------------------------------------- */

// Heads a block of the detail: "Over deze afspraak", "Meer info over je geplande opname?".
type AgendaDetailSectionTitleProps = ComponentPropsWithoutRef<typeof Span>

const AgendaDetailSectionTitle = ({ children, ...props }: AgendaDetailSectionTitleProps) => (
  <Span type='BodyMediumBold' {...props}>
    {children}
  </Span>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Text
 * ------------------------------------------------------------------------------------------------- */

// Supporting copy under a section title, in the secondary text colour.
type AgendaDetailTextProps = ComponentPropsWithoutRef<typeof Span>

const AgendaDetailText = ({ children, ...props }: AgendaDetailTextProps) => (
  <StText type='BodyMediumRegular' {...props}>
    {children}
  </StText>
)

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Notes
 * ------------------------------------------------------------------------------------------------- */

// Free text from the hospital, which arrives as HTML. The part sanitises it itself so that no call
// site can forget to, gives the markup its typography and keeps it selectable so a patient can copy
// an instruction.
type AgendaDetailNotesProps = Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'dangerouslySetInnerHTML'> & {
  html: string
}

const AgendaDetailNotes = ({ html, ...props }: AgendaDetailNotesProps) => {
  const sanitized = useMemo(() => DOMPurify.sanitize(html), [html])

  // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify above
  return <StNotes {...props} dangerouslySetInnerHTML={{ __html: sanitized }} />
}

/* ------------------------------------------------------------------------------------------------- *
 * AgendaDetail.Cards
 * ------------------------------------------------------------------------------------------------- */

// Equal-width cards: stacked on a phone, two columns from the M breakpoint. The column count is
// fixed so a single card takes half the panel instead of stretching across it. A part rather than
// call-site layout because the kit's Grid is inline-style and cannot switch on a breakpoint.
type AgendaDetailCardsProps = ComponentPropsWithoutRef<'div'>

const AgendaDetailCards = ({ children, ...props }: AgendaDetailCardsProps) => <StCards {...props}>{children}</StCards>

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const AgendaDetail = Object.assign(AgendaDetailRoot, {
  Title: AgendaDetailTitle,
  Icon: AgendaDetailIcon,
  Row: AgendaDetailRow,
  Fact: AgendaDetailFact,
  WaitTime: AgendaDetailWaitTime,
  SectionTitle: AgendaDetailSectionTitle,
  Text: AgendaDetailText,
  Notes: AgendaDetailNotes,
  Cards: AgendaDetailCards,
})

export type { AgendaDetailTone }
export { AgendaDetail }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The default text colour of the whole detail; parts inherit it unless a row or a prop says otherwise.
const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

// The heading's typography sets display: inline-block, so the stacking is restated after it.
const StTitle = styled(H2)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
`

const StEyebrow = styled.span`
  ${({ theme }) => theme.Typography.Heading3}
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StIcon = styled(FontAwesomeIcon)`
  width: 16px;
  height: 16px;
`

// One line of a fact tall: the icon centres in it, so it lines up with the fact's first line.
const StIconSlot = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: ${({ theme }) => theme.Typography.BodyLargeRegular['line-height']};
`

const StRow = styled.div<{ $tone: AgendaDetailTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};

  ${({ theme, $tone }) => {
    switch ($tone) {
      case 'highlight':
        return `
          color: ${theme.Components.Button.Tertiary.Text};
          padding: ${theme.UI.SpacingPx.Space2};
          margin: 0 -${theme.UI.SpacingPx.Space2};
          border-radius: ${theme.UI.BorderRadius.Small}px;
          background-color: ${theme.Components.Button.Grey.Fill};
        `
      case 'warning':
        return `color: ${theme.Colors.Status.Error};`
      default:
        return ''
    }
  }}
`

// The parts with a colour of their own keep it as a default the colour prop can still replace.
const StWaitTime = styled(Span)`
  color: ${({ theme, color }) => color ?? theme.Colors.Status.Error};
`

const StText = styled(Span)`
  color: ${({ theme, color }) => color ?? theme.Colors.Neutrals.Darker};
`

// The notes read in the same colour as every other text of the detail.
const StNotes = styled.div`
  ${({ theme }) => theme.Typography.BodyMediumRegular}
  display: block;
  word-break: break-word;
  -webkit-touch-callout: text;
  -webkit-user-select: text;
  user-select: text;

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }

  /* A heading in the hospital's text is a bold line, not a page heading */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${({ theme }) => theme.Typography.BodyMediumBold}
  }

  * + p,
  * + ul,
  * + h1,
  * + h2,
  * + h3,
  * + h4,
  * + h5,
  * + h6 {
    margin-top: ${({ theme }) => theme.UI.SpacingPx.Space3};
  }

  /* The bullet sits inside the line, flush with the text, as the design draws it; the app's reset
     has removed the list's own padding, which would otherwise hang the bullet outside the notes. */
  ul {
    margin: 0;
    padding: 0;
    list-style: disc inside;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`

const StCards = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};

  @media ${breakpoints.fromM} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
